import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, ChevronLeft, Network, MonitorSmartphone, GraduationCap, Store, Sparkles,
  UserCog, CreditCard, ShieldCheck, LayoutDashboard, Headphones, Search, CheckCircle2,
} from "lucide-react";

const PILAR = {
  red: "#2563EB",
  software: "#0891B2",
  academia: "#7E22CE",
  marketplace: "#D97706",
  ia: "#6366F1",
};

// Índice del manual (scroll a anclas)
const TOC = [
  { id: "que-es", label: "1. ¿Qué es ConstruLink?" },
  { id: "familia", label: "2. La Familia ConstruLink" },
  { id: "primeros-pasos", label: "3. Primeros pasos" },
  { id: "pilar-red", label: "4. Pilar 01 · Red" },
  { id: "pilar-software", label: "5. Pilar 02 · Software" },
  { id: "pilar-academia", label: "6. Pilar 03 · Academia" },
  { id: "pilar-marketplace", label: "7. Pilar 04 · Marketplace" },
  { id: "pilar-ia", label: "8. Pilar 05 · IA" },
  { id: "membresias", label: "9. Membresías y beneficios" },
  { id: "pagos", label: "10. Pagos y facturación" },
  { id: "dashboard", label: "11. Tu Panel (Dashboard)" },
  { id: "seguridad", label: "12. Seguridad y cuenta" },
  { id: "soporte", label: "13. Soporte" },
];

// --- Componentes de contenido reutilizables ---
const Section = ({ id, icon: Icon, color = "#1E3A8A", eyebrow, title, children }) => (
  <section id={id} className="scroll-mt-24 bg-white rounded-2xl border border-slate-100 shadow-soft p-6 md:p-8" data-testid={`manual-sec-${id}`}>
    <div className="flex items-center gap-3">
      <span className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15`, color }}>
        <Icon size={22} />
      </span>
      <div>
        {eyebrow && <p className="text-xs font-bold uppercase tracking-wide" style={{ color }}>{eyebrow}</p>}
        <h2 className="text-xl md:text-2xl font-extrabold text-slate-900 leading-tight">{title}</h2>
      </div>
    </div>
    <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-slate-600">{children}</div>
  </section>
);

const H3 = ({ children }) => <h3 className="text-slate-900 font-bold text-base mt-5 mb-1">{children}</h3>;

const Steps = ({ items }) => (
  <ol className="mt-2 space-y-2">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-0.5 w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
        <span>{t}</span>
      </li>
    ))}
  </ol>
);

const Bullets = ({ items, color = "#16A34A" }) => (
  <ul className="mt-2 space-y-1.5">
    {items.map((t, i) => (
      <li key={i} className="flex gap-2.5">
        <CheckCircle2 size={17} className="mt-0.5 flex-shrink-0" style={{ color }} />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const Callout = ({ children, color = "#1E3A8A" }) => (
  <div className="mt-3 rounded-xl border-l-4 p-3 text-sm" style={{ borderColor: color, backgroundColor: `${color}0D` }}>{children}</div>
);

export default function ManualPage() {
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const toc = TOC.filter((t) => !ql || t.label.toLowerCase().includes(ql));

  return (
    <div className="px-5 md:px-8 py-8 max-w-6xl mx-auto" data-testid="manual-page" style={{ scrollBehavior: "smooth" }}>
      <Link to="/centro-ayuda" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Centro de Ayuda
      </Link>

      {/* Portada */}
      <header className="mt-4 rounded-3xl overflow-hidden border border-slate-200 bg-gradient-to-br from-[#0B1F3A] to-[#1E3A8A] text-white p-8 md:p-10 relative">
        <div className="absolute -right-10 -top-10 opacity-10"><BookOpen size={220} /></div>
        <p className="text-xs font-bold uppercase tracking-widest text-blue-200">Guía oficial de uso</p>
        <h1 className="mt-2 text-3xl md:text-5xl font-extrabold tracking-tight inline-flex items-center gap-3">
          <BookOpen size={38} className="text-blue-200" /> Manual ConstruLink
        </h1>
        <p className="mt-3 max-w-2xl text-blue-100 text-base">
          Todo lo que necesitas saber sobre el ecosistema ConstruLink y su Familia de 5 pilares:
          Red, Software, Academia, Marketplace e IA. Un manual de instrucciones paso a paso.
        </p>
      </header>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8 items-start">
        {/* Índice (TOC) */}
        <aside className="lg:sticky lg:top-6" data-testid="manual-toc">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4">
            <div className="relative mb-3">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="manual-buscar" placeholder="Buscar sección…"
                className="w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 px-2 mb-1">Contenido</p>
            <nav className="space-y-0.5">
              {toc.map((t) => (
                <a key={t.id} href={`#${t.id}`} data-testid={`manual-toc-${t.id}`}
                  className="block px-2 py-1.5 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                  {t.label}
                </a>
              ))}
              {toc.length === 0 && <p className="px-2 py-2 text-sm text-slate-400">Sin resultados.</p>}
            </nav>
          </div>
        </aside>

        {/* Contenido */}
        <div className="space-y-5">
          <Section id="que-es" icon={BookOpen} eyebrow="Introducción" title="¿Qué es ConstruLink?">
            <p>
              <b>ConstruLink</b> es una <b>plataforma integral para la industria de la construcción</b>. Reúne, en un solo lugar y con una sola cuenta
              (<b>ConstruLink ID</b>), las herramientas que un profesional, empresa o cliente necesita para conectar, trabajar, aprender,
              vender y crecer. En lugar de usar apps separadas, ConstruLink integra todo en un ecosistema unificado de 5 soluciones o «pilares».
            </p>
            <Callout>
              Una sola cuenta te da acceso a los 5 pilares. Tu perfil, tus verificaciones y tu plan de membresía te acompañan en todo el ecosistema.
            </Callout>
          </Section>

          <Section id="familia" icon={Network} eyebrow="Visión general" title="La Familia ConstruLink (5 pilares)">
            <p>Estas son las cinco soluciones que conforman el ecosistema:</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              {[
                { c: PILAR.red, I: Network, t: "01 · Red", d: "Red profesional y marketplace de conexión: clientes, maestros-contratistas y ferreterías." },
                { c: PILAR.software, I: MonitorSmartphone, t: "02 · Software", d: "Catálogo de software y recursos digitales para la construcción con descarga segura." },
                { c: PILAR.academia, I: GraduationCap, t: "03 · Academia", d: "Cursos en línea con progreso, gamificación y certificados verificables." },
                { c: PILAR.marketplace, I: Store, t: "04 · Marketplace", d: "Tienda de productos digitales de vendedores independientes." },
                { c: PILAR.ia, I: Sparkles, t: "05 · IA", d: "Inteligencia artificial para la construcción (próximamente)." },
              ].map((p) => (
                <div key={p.t} className="rounded-xl border border-slate-100 p-4 flex gap-3">
                  <span className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${p.c}15`, color: p.c }}><p.I size={20} /></span>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{p.t}</p>
                    <p className="text-sm text-slate-600">{p.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section id="primeros-pasos" icon={UserCog} eyebrow="Empezar" title="Primeros pasos">
            <H3>Crear tu cuenta e iniciar sesión</H3>
            <Steps items={[
              "Regístrate con tu correo (o «Continuar con Google») en la pantalla de acceso.",
              "Elige tu rol: Cliente, Maestro-Contratista o Ferretería. Esto adapta tu experiencia.",
              "Verifica tu correo y, opcionalmente, tu teléfono desde Mi Perfil → Verificaciones.",
            ]} />
            <H3>Completar tu perfil</H3>
            <p>Un perfil completo genera más confianza y visibilidad. Desde <b>Mi Perfil</b> encontrarás:</p>
            <Bullets items={[
              "Perfil Global: nombre, foto, ubicación (regiones/provincias/distritos del Perú) y descripción.",
              "Perfil Profesional: especialidad, experiencia, servicios y datos de empresa (RUC).",
              "Portafolio: tu vitrina de trabajos, fotos, documentos y certificados.",
              "Verificaciones: correo, teléfono, empresa y colegio profesional para subir tu nivel de confianza.",
            ]} />
            <Callout color={PILAR.academia}>El anillo de progreso de tu perfil te indica qué falta para completarlo al 100%.</Callout>
          </Section>

          {/* PILAR 01 */}
          <Section id="pilar-red" icon={Network} color={PILAR.red} eyebrow="Pilar 01" title="ConstruLink Red — Plataforma de Conexión">
            <p>
              Es la red profesional y el marketplace de servicios de la construcción. Conecta a <b>Clientes</b> que buscan ejecutar proyectos,
              <b> Maestros-Contratistas</b> que ofrecen servicios y <b>Ferreterías</b> que venden materiales.
            </p>
            <H3>¿Qué puedes hacer?</H3>
            <Bullets items={[
              "Publicar oportunidades, servicios o solicitudes de materiales (Red → Publicar).",
              "Buscar y filtrar por región, provincia, distrito y texto; las publicaciones destacadas aparecen primero.",
              "Chatear en tiempo real con otros miembros (adjuntos, notas de voz, confirmación de lectura y presencia en línea).",
              "Ver el Perfil Público / Vitrina de cualquier miembro y contactarlo.",
              "Recibir notificaciones de mensajes, ofertas de interés y verificaciones.",
            ]} color={PILAR.red} />
            <H3>Cómo publicar</H3>
            <Steps items={[
              "Entra a Red → Publicar.",
              "Completa título, descripción, categoría y ubicación.",
              "Publica: tu anuncio queda visible en el buscador para toda la comunidad.",
              "Opcional: «Destacar» tu publicación para aparecer primero durante 3 a 30 días.",
            ]} />
          </Section>

          {/* PILAR 02 */}
          <Section id="pilar-software" icon={MonitorSmartphone} color={PILAR.software} eyebrow="Pilar 02" title="ConstruLink Software">
            <p>
              Catálogo de <b>software y recursos digitales</b> para la construcción: plantillas de metrados y presupuestos (Excel/PDF)
              y programas/aplicaciones (APK). Cada producto tiene ficha, precio y descarga protegida.
            </p>
            <H3>Comprar y descargar</H3>
            <Steps items={[
              "Explora el catálogo en el Pilar Software y abre la ficha del producto.",
              "Compra con Mercado Pago (si tienes plan Pro/Elite verás el precio con descuento aplicado).",
              "Tras confirmarse el pago, el producto aparece en Software → Mis Descargas.",
              "Descarga el archivo mediante un enlace seguro vinculado a tu cuenta (no se comparten URLs directas).",
            ]} />
            <Callout color={PILAR.software}>Algunos productos incluyen archivo de licencia/contraseña, que se entrega solo al comprador.</Callout>
          </Section>

          {/* PILAR 03 */}
          <Section id="pilar-academia" icon={GraduationCap} color={PILAR.academia} eyebrow="Pilar 03" title="ConstruLink Academia">
            <p>
              Plataforma de <b>cursos en línea</b> con aula virtual estilo profesional: video protegido, temario por módulos y lecciones,
              seguimiento de progreso, gamificación y <b>certificados oficiales verificables</b>.
            </p>
            <H3>Cómo estudiar</H3>
            <Steps items={[
              "Inscríbete en un curso (gratuito o de pago con Mercado Pago).",
              "Accede desde Academia → Mis Cursos y entra al aula virtual.",
              "Avanza por las lecciones; tu progreso se guarda automáticamente y con «Concluir» sumas avance.",
              "Al llegar al 100% se genera tu certificado PDF y recibes un correo de felicitación.",
            ]} />
            <H3>Certificados</H3>
            <Bullets items={[
              "Diseño premium de marca con QR y código único de verificación.",
              "Disponibles en Academia → Mis Certificados (con vista previa antes de descargar).",
              "Cualquiera puede validar su autenticidad ingresando el código, sin necesidad de iniciar sesión.",
            ]} color={PILAR.academia} />
          </Section>

          {/* PILAR 04 */}
          <Section id="pilar-marketplace" icon={Store} color={PILAR.marketplace} eyebrow="Pilar 04" title="ConstruLink Marketplace">
            <p>
              Un <b>mercado de productos digitales</b> donde vendedores independientes publican plantillas, recursos, software y cursos.
              Incluye tienda propia del vendedor, carrito, checkout, comisiones y moderación administrativa.
            </p>
            <H3>Si compras</H3>
            <Steps items={[
              "Explora el catálogo público y agrega productos al carrito.",
              "Acepta los Términos y la Política de reembolsos y paga con Mercado Pago.",
              "Encuentra tus recursos en «Mis Compras», con descarga protegida.",
              "Reembolso: solo dentro de los 3 días de la compra y si aún no descargaste el material (devolución hasta en 30 días).",
            ]} />
            <H3>Si vendes</H3>
            <Steps items={[
              "Crea tu tienda desde Marketplace → Crear tienda y envíala a revisión.",
              "Publica productos o cursos; pasan por moderación antes de estar visibles al público.",
              "Consulta tus ventas, comisión y saldo en Marketplace → Comisiones.",
              "Solicita el retiro de tus ganancias; el administrador lo revisa y registra el pago.",
            ]} />
            <Callout color={PILAR.marketplace}>
              Los cursos y productos del Marketplace son de vendedores independientes y no forman parte del catálogo oficial de Academia/Software.
            </Callout>
          </Section>

          {/* PILAR 05 */}
          <Section id="pilar-ia" icon={Sparkles} color={PILAR.ia} eyebrow="Pilar 05" title="ConstruLink IA — Próximamente">
            <p>
              El pilar de <b>Inteligencia Artificial para la construcción</b> está en desarrollo. Su objetivo será asistir con tareas como
              categorización automática de publicaciones, sugerencias de descripción, y emparejamiento inteligente entre clientes y contratistas.
            </p>
            <Callout color={PILAR.ia}>Te avisaremos dentro de la plataforma cuando esta solución esté disponible.</Callout>
          </Section>

          {/* Membresías */}
          <Section id="membresias" icon={CreditCard} color="#16A34A" eyebrow="Planes" title="Membresías y beneficios">
            <p>ConstruLink ofrece tres planes que potencian tu experiencia en todo el ecosistema:</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-2">
              {[
                { t: "Básico", p: "Gratis", d: "Acceso al ecosistema y funciones esenciales." },
                { t: "Pro", p: "S/ 29.90/mes", d: "15% de descuento en Software y Academia, insignia Pro y más visibilidad." },
                { t: "Elite", p: "S/ 59.90/mes", d: "30% de descuento, insignia Elite dorada y 3 publicaciones destacadas gratis al mes." },
              ].map((m) => (
                <div key={m.t} className="rounded-xl border border-slate-100 p-4">
                  <p className="font-extrabold text-slate-900">{m.t}</p>
                  <p className="text-sm font-bold text-green-600">{m.p}</p>
                  <p className="text-sm text-slate-600 mt-1">{m.d}</p>
                </div>
              ))}
            </div>
            <p className="mt-3">Cambia o mejora tu plan cuando quieras desde <b>Planes y Membresías</b>. Los descuentos se aplican automáticamente a los precios.</p>
          </Section>

          {/* Pagos */}
          <Section id="pagos" icon={CreditCard} color="#16A34A" eyebrow="Cobros" title="Pagos y facturación">
            <Bullets items={[
              "Pagamos con Mercado Pago: tarjetas y medios locales (Yape, Plin, PagoEfectivo según disponibilidad).",
              "Los precios se muestran en la moneda de tu región.",
              "El historial de pagos, órdenes y comprobantes está en «Pagos y Facturación».",
              "Recibes un comprobante por correo al confirmarse cada pago.",
            ]} />
          </Section>

          {/* Dashboard */}
          <Section id="dashboard" icon={LayoutDashboard} color="#1E3A8A" eyebrow="Tu espacio" title="Tu Panel (Dashboard)">
            <p>Desde el menú lateral gestionas toda tu actividad:</p>
            <Bullets items={[
              "Notificaciones y Mensajes: mantente al día de la actividad de tu cuenta.",
              "Mis Pedidos, Pagos y Facturación: tu historial comercial.",
              "Mi Inventario y Cotizaciones: gestión para negocios y ferreterías.",
              "Mis Clientes: tu pequeña agenda/CRM de contactos.",
              "Favoritos: guarda productos para volver a ellos.",
              "Configuración y Seguridad: preferencias, contraseña y registro de actividad.",
            ]} color="#1E3A8A" />
          </Section>

          {/* Seguridad */}
          <Section id="seguridad" icon={ShieldCheck} color="#0891B2" eyebrow="Protección" title="Seguridad y cuenta">
            <Bullets items={[
              "Inicio de sesión seguro con ConstruLink ID (token de sesión) y protección contra intentos fallidos.",
              "Cambia tu contraseña y revisa tu registro de actividad desde Seguridad.",
              "Las descargas de Software y los videos de Academia están protegidos y vinculados a tu cuenta.",
              "Nunca compartas tu contraseña; ConstruLink no te la pedirá por correo.",
            ]} color="#0891B2" />
          </Section>

          {/* Soporte */}
          <Section id="soporte" icon={Headphones} color="#0891B2" eyebrow="Ayuda" title="Soporte">
            <p>¿Necesitas ayuda adicional? Tienes varios canales:</p>
            <Bullets items={[
              "Centro de Ayuda: preguntas frecuentes por categoría.",
              "Soporte: envía una consulta y sigue tus tickets.",
              "Correo y WhatsApp: canales directos disponibles en la página de Soporte.",
            ]} color="#0891B2" />
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to="/centro-ayuda" data-testid="manual-ir-ayuda" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 font-semibold text-sm text-slate-700 hover:bg-slate-50">
                <LifeBuoyMini /> Centro de Ayuda
              </Link>
              <Link to="/soporte" data-testid="manual-ir-soporte" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white font-semibold text-sm hover:brightness-110" style={{ backgroundColor: "#0891B2" }}>
                <Headphones size={16} /> Contactar Soporte
              </Link>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

const LifeBuoyMini = () => <Headphones size={16} className="text-slate-500" />;
